import type { Access, MoneyLevel } from './schemas';

export interface AccessTarget {
  legalEntityId?: string | null;
  directionId?: string | null;
  projectId?: string | null;
  /** Проекты выше по дереву (для подпроекта — его проект): доступ сверху открывает подпроект (ПФ-20) */
  projectPath?: Array<string | null | undefined> | null;
}

/**
 * Проверка права с учётом области. Права наследуются сверху вниз: компания целиком →
 * юрлицо → направление → проект → подпроект; доступ к подпроекту не открывает соседние.
 * Одна функция для сервера и для интерфейса.
 */
export function can(
  access: Access | null | undefined,
  permission: string,
  target: AccessTarget = {},
): boolean {
  if (!access) return false;
  if (
    access.externalProjectIds &&
    (!target.projectId || !access.externalProjectIds.includes(target.projectId))
  )
    return false;
  if (access.tenant.includes(permission)) return true;
  if (target.legalEntityId && access.legalEntities[target.legalEntityId]?.includes(permission)) return true;
  if (target.directionId && access.directions?.[target.directionId]?.includes(permission)) return true;
  if (target.projectId && access.projects[target.projectId]?.includes(permission)) return true;
  for (const id of access.externalProjectIds ? [] : (target.projectPath ?? [])) {
    if (id && access.projects[id]?.includes(permission)) return true;
  }
  return false;
}

/** Есть ли право хоть где-нибудь (для меню и списков, которые сами фильтруют записи). */
export function canAnywhere(access: Access | null | undefined, permission: string): boolean {
  if (!access) return false;
  if (access.tenant.includes(permission)) return true;
  for (const perms of Object.values(access.legalEntities)) if (perms.includes(permission)) return true;
  for (const perms of Object.values(access.directions ?? {})) if (perms.includes(permission)) return true;
  for (const perms of Object.values(access.projects)) if (perms.includes(permission)) return true;
  return false;
}

/**
 * Право на уровне компании или юрлица — для общих справочников группы (реестр контрагентов):
 * право на один проект или направление для них не подходит.
 */
export function canCompanyWide(access: Access | null | undefined, permission: string): boolean {
  if (!access) return false;
  return (
    access.tenant.includes(permission) ||
    Object.values(access.legalEntities).some((perms) => perms.includes(permission))
  );
}

/**
 * Право на уровне всей компании (группы юрлиц) — для настроек и справочников, общих для всех юрлиц:
 * лимиты платежей, комплекты документов к оплате. Право на одно юрлицо их не открывает.
 */
export function canTenant(access: Access | null | undefined, permission: string): boolean {
  return Boolean(access?.tenant.includes(permission));
}

const MONEY_ORDER: MoneyLevel[] = ['none', 'warehouse', 'acts', 'acts_invoices', 'all'];

/**
 * Уровень денег человека в записи этой области (ДС-18): наибольший из общего уровня и уровней юрлица,
 * направления, проекта и проекта над подпроектом. Без области — только общий уровень: место, которое ещё
 * не передаёт область записи, покажет меньше, а не больше.
 */
export function moneyAt(access: Access | null | undefined, target?: AccessTarget | null): MoneyLevel {
  if (!access) return 'none';
  let best = MONEY_ORDER.indexOf(access.money);
  const m = access.moneyIn;
  if (!m || !target) return MONEY_ORDER[Math.max(best, 0)]!;
  const take = (lvl: MoneyLevel | undefined) => {
    if (lvl) best = Math.max(best, MONEY_ORDER.indexOf(lvl));
  };
  const ext = access.externalProjectIds;
  // Внешний участник — только в своих проектах и только по уровню самого проекта (без наследования)
  if (ext) {
    if (target.projectId && ext.includes(target.projectId)) take(m.projects[target.projectId]);
    return MONEY_ORDER[Math.max(best, 0)]!;
  }
  if (target.legalEntityId) take(m.legalEntities[target.legalEntityId]);
  if (target.directionId) take(m.directions[target.directionId]);
  if (target.projectId) take(m.projects[target.projectId]);
  for (const id of target.projectPath ?? []) if (id) take(m.projects[id]);
  return MONEY_ORDER[Math.max(best, 0)]!;
}

/**
 * Видит ли человек суммы нужного уровня (ДС-04, ДС-05). С областью записи — с учётом уровня денег
 * на проекте, направлении или юрлице (ДС-18); без области — только общий уровень.
 */
export function seesMoney(
  access: Access | null | undefined,
  level: MoneyLevel = 'all',
  target?: AccessTarget | null,
): boolean {
  if (!access) return false;
  return MONEY_ORDER.indexOf(moneyAt(access, target)) >= MONEY_ORDER.indexOf(level);
}

/** Есть ли у человека уровень денег выше общего хоть в одной области (ДС-18) */
export function hasScopedMoney(access: Access | null | undefined, level: MoneyLevel = 'all'): boolean {
  const m = access?.moneyIn;
  if (!m) return false;
  const need = MONEY_ORDER.indexOf(level);
  return [m.legalEntities, m.directions, m.projects].some((map) =>
    Object.values(map).some((l) => MONEY_ORDER.indexOf(l) >= need),
  );
}

/** Сравнение уровней денег: какой выше */
export function moneyRank(level: MoneyLevel): number {
  return MONEY_ORDER.indexOf(level);
}

export function maxMoney(a: MoneyLevel, b: MoneyLevel): MoneyLevel {
  return moneyRank(a) >= moneyRank(b) ? a : b;
}

export function minMoney(a: MoneyLevel, b: MoneyLevel): MoneyLevel {
  return moneyRank(a) <= moneyRank(b) ? a : b;
}

/**
 * Согласования и решения о деньгах (ДС-07, УВ-10): договоры и их подписание, выбор сверх лимита, факт оплаты, бюджет,
 * смета, изменения, складские решения о потерях и недостачах, факт оплаты в платёжном календаре, решения финансового
 * менеджера, а также управление людьми и ролями — через них можно выдать себе любое из этих прав. Заместитель получает эти
 * права от замещаемого, только если видит те же суммы, что видел бы замещаемый; иначе согласование остаётся у
 * следующего по маршруту и у других согласующих с этим правом.
 */
export const MONEY_DECISION_PERMS: readonly string[] = [
  'contracts.approve',
  'contracts.approve.accounting',
  'contracts.approve.legal',
  'contracts.approve.deputy',
  'contracts.approve.security',
  'contracts.sign',
  'procurement.approve.limit',
  'procurement.payment.confirm',
  'budget.approve',
  'estimate.approve',
  'changes.approve',
  'warehouse.approve',
  'finance.edit',
  'finance.manager',
  'admin.users',
  'admin.roles',
];

/** Наибольший уровень денег человека в любой области: общий и по юрлицам, направлениям, проектам (ДС-18) */
export function topMoney(access: Access | null | undefined): MoneyLevel {
  if (!access) return 'none';
  let best = access.money;
  const m = access.moneyIn;
  if (m)
    for (const map of [m.legalEntities, m.directions, m.projects])
      for (const lvl of Object.values(map)) best = maxMoney(best, lvl);
  return best;
}
